import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import MultiEmailSelect from "../../../components/ChangeSelect";
import {
  communityService,
  groupService,
  invitationService,
  membershipService,
  ServiceOnMount,
} from "../../../services";
import { Skeleton } from "../../../components/ui/skeleton";
import { GrGroup } from "react-icons/gr";
import Modal from "../../../components/Modal";
import useUserStore from "../../../stores/userStore";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaPencilAlt,
  FaTrashAlt,
} from "react-icons/fa";
import LoadingImage from "../../../components/LoadingImage";
import { DataTable } from "../../../components/DataTable";
import { toast } from "react-toastify";
import StatusBadge from "../../../components/table/StatusBadge"

const columns = [
  // {
  //   accessorKey: "groupId",
  //   header: "Group ID",
  //   cell: ({ row }) => (
  //     <span>
  //       <ShortenedText text={row.getValue("groupId")} />
  //     </span>
  //   ),
  // },
  {
    accessorKey: "groupName",
    header: "Group Name",
    cell: ({ row }) => <span>{row.getValue("groupName")}</span>,
  },
  {
    accessorKey: "groupLeader",
    header: "Group Leader",
    cell: ({ row }) => <span>{row.getValue("groupLeader")}</span>,
  },
  {
    accessorKey: "totalMembers",
    header: "Total Members",
    cell: ({ row }) => <span>{row.getValue("totalMembers")}</span>,
  },
  {
    accessorKey: "dateCreated",
    header: "Date Created",
    cell: ({ row }) => <span>{row.getValue("dateCreated")}</span>,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status");
      return (
        <StatusBadge status={status} />
      );
    },
  },
];

const EditCommunityPage = () => {
  const { communityId } = useParams(); // To get the community ID from the URL
  const navigate = useNavigate();
  const location = useLocation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [groupToActivate, setGroupToActivate] = useState(null);
  const [loading, setLoading] = useState(false);
  const { userId } = useUserStore();
  const [reload, setReload] = useState(false);
  const [adminEmails, setAdminEmails] = useState([]);
  // const { setBreadcrumbData } = useOutletContext();

  const actions = (row) => [
    {
      label: "View/Edit Details",
      icon: <FaPencilAlt />,
      onClick: () => {
        navigate(`/group-management/edit/${row.groupId}`);
      },
      variant: "dropPlain",
    },
    {
      label: "Activate Group",
      icon: <FaCheckCircle />,
      onClick: () => {
        setGroupToActivate(row.groupId);
        setShowActivateModal(true);
      },
      variant: "dropGreen",
    },
    {
      label: "Deactivate Group",
      icon: <FaTrashAlt />,
      onClick: () => {
        setGroupToDelete(row.groupId);
        setShowDeleteModal(true);
      },
      variant: "dropRed",
    },
  ];

  const handleConfirmActivate = async () => {
    if (!groupToActivate) return;
    setLoading(true);

    try {
      const payload = {
        EntityId: userId, // assuming userId is available in the scope
        GroupId: groupToActivate, // assuming groupToDelete is just the group ID
        Status: "A",
      };

      await groupService.editGroup(payload);

      toast.success("🎉 Group has been activated successfully!", {
        autoClose: 2000,
      });
      setReload((prev) => !prev); // trigger table refresh
    } catch (error) {
      console.error("Activation failed:", error);
      toast.error("❌ Failed to activate group", {
        autoClose: 2000,
      });
    } finally {
      setShowActivateModal(false);
      setLoading(false);
      setGroupToActivate(null);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!groupToDelete) return;
    setLoading(true);

    try {
      const payload = {
        EntityId: userId, // assuming userId is available in the scope
        GroupId: groupToDelete, // assuming groupToDelete is just the group ID
        Status: "D",
      };

      await groupService.editGroup(payload);

      toast.success("🎉 Group has been deactivated successfully!", {
        autoClose: 2000,
      });
      setReload((prev) => !prev); // trigger table refresh
    } catch (error) {
      console.error("Deactivation failed:", error);
      toast.error("❌ Failed to deactivate group", {
        autoClose: 2000,
      });
    } finally {
      setShowDeleteModal(false);
      setLoading(false);
      setGroupToDelete(null);
    }
  };

  const dataMap = (item) => {
    // Create an array of admin objects with all related data
    const admins = item.admins
      .map((admin) => {
        const baseEmail = admin.entity?.email;
        if (!baseEmail) return null;

        // Standardize pending format - always use "(Pending)" with capital P
        const email = admin.profile ? baseEmail : `${baseEmail} (Pending)`;

        return {
          email,
          entityId: admin.entity?._id || null,
          membershipId: admin._id || null,
          profileId: admin.profile?._id || null,
          profileName: admin.profile?.name || null,
          rawEmail: baseEmail, // Store the original email without pending marker
        };
      })
      .filter((admin) => admin !== null);

    return {
      communityID: item._id,
      name: item.name,
      status:
        item.status === "A"
          ? "Active"
          : item.status === "P"
          ? "Pending"
          : "Dormant",
      // Store the full admin objects array for reference
      admins: admins,
      adminEmails: admins.map((admin) => admin.email).join(", "),
      // For display purposes, create comma-separated strings
      profileID: admins
        .map((admin) => admin.profileId || "Not Available")
        .join(", "),
      membershipId: admins
        .map((admin) => admin.membershipId || "Not Available")
        .join(", "),
      adminID: admins
        .map((admin) => admin.entityId || "Not Available")
        .join(", "),
      adminName: admins
        .map((admin) => admin.profileName || "Not Available")
        .join(", "),

      description: item.description,
      createdDate: item.create_date
        ? new Date(item.create_date).toLocaleDateString()
        : "Not Available",
      address_line1: item.address_line1 || "Not Available",
      address_line2: item.address_line2 || "Not Available",
      postcode: item.postcode || "Not Available",
      city: item.city || "Not Available",
      state: item.state || "Not Available",
      country: item.country || "Not Available",
    };
  };

  const getCommunityDetails = ServiceOnMount(
    () => communityService.getCommunityDetails(communityId),
    dataMap
  );
  const communityData = getCommunityDetails.data?.[0] || {};
  const isLoading = getCommunityDetails.loading;

  console.log("comunity data: ", communityData);

  const groupMap = (item) => ({
    groupId: item._id,
    groupName: item.name,
    communityName: item.community?.name || "N/A",
    communityId: item.community?._id || "N/A",
    status:
      item?.status === "A"
        ? "Active"
        : item?.status === "D"
        ? "Dormant"
        : item?.status,
    groupLeader:
      item.leaders && item.leaders.length > 0
        ? item.leaders
            .map((leader) => leader.profile?.name)
            .filter(Boolean) // remove any undefined/null names
            .join(", ")
        : "N/A",
    totalMembers: item.total,
    dateCreated: item.create_date
      ? new Date(item.create_date).toLocaleDateString()
      : "N/A",
  });
  const getGroupDetails = ServiceOnMount(
    () => groupService.getGroups(communityId),
    groupMap,
    [reload]
  );

  const groupData = getGroupDetails.data;

  const [activeTab, setActiveTab] = useState("general");
  const [generalInfo, setGeneralInfo] = useState(() => ({
    communityName: communityData?.name || "",
    adminEmails: "",
    description: communityData?.description || "",
    adminName: communityData?.adminName || "",
  }));

  const [addressInfo, setAddressInfo] = useState(() => ({
    addressLine1: communityData?.address_line1 || "",
    addressLine2: communityData?.address_line2 || "",
    postcode: communityData?.postcode || "",
    city: communityData?.city || "",
    state: communityData?.state || "",
    country: communityData?.country || "",
  }));

  const [status, setStatus] = useState("");

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  const handleGeneralInfoChange = (e) => {
    const { name, value } = e.target;
    setGeneralInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddressInfoChange = (e) => {
    const { name, value } = e.target;
    setAddressInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const currentCommunity = getCommunityDetails.data?.[0] || {};
      const currentAdmins = currentCommunity.admins || [];

      // Create normalized versions of current admins for comparison
      const normalizedCurrentAdmins = currentAdmins.map((admin) => ({
        email: admin.email,
        rawEmail: admin.rawEmail,
        isPending: !admin.profileId, // true if no profile (pending)
      }));

      // Process the adminEmails list to maintain pending status correctly
      const processedAdminEmails = adminEmails.map((email) => {
        // Find if this email exists in current admins
        const existingAdmin = normalizedCurrentAdmins.find(
          (a) =>
            a.rawEmail.toLowerCase() ===
            email.replace(/\s*\([pP]ending\)\s*/g, "").toLowerCase()
        );

        // If it exists and is pending, keep the pending marker
        if (existingAdmin && existingAdmin.isPending) {
          return existingAdmin.email.includes("(Pending)")
            ? existingAdmin.email
            : `${existingAdmin.rawEmail} (Pending)`;
        }

        // Otherwise return just the clean email
        return email.replace(/\s*\([pP]ending\)\s*/g, "");
      });

      // Update the adminEmails state with properly formatted emails
      setAdminEmails(processedAdminEmails);

      // Now proceed with the comparison using the processed emails
      const normalizedNewEmails = processedAdminEmails.map((email) =>
        email.replace(/\s*\([pP]ending\)\s*/g, "").toLowerCase()
      );

      // Rest of your handleSave function remains the same...
      // (the part that handles removed admins, added admins, etc.)

      // Create a Set of normalized current admin emails for quick lookup
      const currentAdminEmails = new Set(
        normalizedCurrentAdmins.map((admin) => admin.rawEmail.toLowerCase())
      );

      // Find admins that were removed (in current but not in new)
      const removedAdmins = normalizedCurrentAdmins.filter(
        (admin) => !normalizedNewEmails.includes(admin.rawEmail.toLowerCase())
      );

      // Find emails that were added (in new but not in current)
      const addedAdmins = normalizedNewEmails.filter(
        (email) => !currentAdminEmails.has(email)
      );

      // 1. Handle removed admins
      const removeAdminPromises = removedAdmins.map(async (admin) => {
        try {
          // If admin is pending, cancel their invite
          if (admin.isPending) {
            await invitationService.cancelInvite({ Email: admin.rawEmail });
            return { type: "canceled", email: admin.rawEmail };
          }

          // Otherwise, demote to regular user
          const adminData = currentAdmins.find(
            (a) => a.rawEmail === admin.rawEmail
          );
          if (!adminData?.membershipId) {
            console.warn(`Missing membershipId for admin ${admin.email}`);
            return { type: "failed", email: admin.rawEmail };
          }

          const payload = {
            EntityId: adminData.entityId,
            MembershipId: adminData.membershipId,
            ProfileId: adminData.profileId || undefined,
            CommunityId: communityId,
            Role: "U",
            Status: "A",
          };

          await membershipService.editMembershipDetails(payload);
          return { type: "demoted", email: admin.rawEmail };
        } catch (error) {
          console.error(`Failed to remove admin ${admin.email}:`, error);
          return { type: "failed", email: admin.rawEmail };
        }
      });

      // 2. Then handle added admins - send them invites
      const addAdminPromises = addedAdmins.map(async (email) => {
        try {
          // Check if this email already exists in any form (with or without pending)
          const alreadyExists = currentAdmins.some(
            (admin) => admin.email.replace(" (Pending)", "") === email
          );

          if (alreadyExists) {
            console.log(`Skipping invite for ${email} - already exists`);
            return false;
          }

          const payload = {
            Email: email,
            Role: "CA",
            CommunityId: communityId,
            Name: "Community Admin",
          };

          await invitationService.sendInvite(payload);
          return true;
        } catch (error) {
          console.error(`Failed to invite admin ${email}:`, error);
          return false;
        }
      });

      // Execute all admin changes in parallel
      const [removeResults, addResults] = await Promise.all([
        Promise.all(removeAdminPromises),
        Promise.all(addAdminPromises),
      ]);

      // Count successful operations
      const successfulRemovals = removeResults.filter(Boolean).length;
      const successfulAdditions = addResults.filter(Boolean).length;

      // 3. Update community details
      const communityPayload = {
        EntityId: "699ETIGY-50HU5K80-GN58S3ZE",
        CommunityId: communityId,
        Name: generalInfo.communityName,
        Description: generalInfo.description,
        AddressLine1: addressInfo.addressLine1,
        AddressLine2: addressInfo.addressLine2,
        Postcode: addressInfo.postcode,
        City: addressInfo.city,
        State: addressInfo.state,
        Country: addressInfo.country,
        Status: status === "Active" ? "A" : status === "Pending" ? "P" : "D",
      };

      await communityService.editCommunityDetails(communityPayload);

      // Show success message with details
      let successMessage = "Community details updated successfully!";
      if (successfulRemovals > 0 || successfulAdditions > 0) {
        successMessage += ` (${successfulAdditions} admin(s) added, ${successfulRemovals} admin(s) removed)`;
      }

      toast.success(`🎉 ${successMessage}`, {
        autoClose: 3000,
      });

      // Refresh data
      setReload((prev) => !prev);
      navigate(`/communities/view/${communityId}`);
    } catch (error) {
      console.error("Error updating community:", error);
      toast.error("❌ Failed to update community details", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (getCommunityDetails.data?.length > 0) {
      const fetchedData = getCommunityDetails.data[0];
      navigate(location.pathname, {
        state: { communityName: fetchedData.name || "" },
        replace: true,
      });

      setGeneralInfo({
        communityName: fetchedData.name || "",
        adminEmails: fetchedData.adminEmails || "",
        description: fetchedData.description || "",
        adminName: fetchedData.adminName || "",
      });

      // Initialize admin emails array
      setAdminEmails(
        fetchedData.adminEmails
          ? fetchedData.adminEmails
              .split(", ")
              .filter((email) => email.trim() !== "")
          : []
      );

      setAddressInfo({
        addressLine1: fetchedData.address_line1 || "",
        addressLine2: fetchedData.address_line2 || "",
        postcode: fetchedData.postcode || "",
        city: fetchedData.city || "",
        state: fetchedData.state || "",
        country: fetchedData.country || "",
      });

      setStatus(
        fetchedData.status === "Active"
          ? "Active"
          : fetchedData.status === "Pending"
          ? "Pending"
          : "Dormant"
      );
    }
  }, [getCommunityDetails.data, navigate, location.pathname]);

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="p-8 flex flex-col">
          <div className="bg-white shadow p-4 pb-1 rounded-lg">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
              {/* Left: Icon + Title + Info */}
              <div className="flex items-center gap-4">
                <div className="bg-teal-100 p-3 sm:p-4 rounded-full">
                  <GrGroup className="w-10 h-10 sm:w-16 sm:h-16 text-teal-700" />
                </div>

                <div>
                  <h1 className="text-base sm:text-lg font-semibold text-[#111827]">
                    {communityData ? (
                      communityData.name
                    ) : (
                      <Skeleton className="w-40 h-6" />
                    )}
                  </h1>
                  <div className="mt-4 sm:mt-6 flex space-x-4 sm:space-x-8">
                    <div>
                      <div className="text-sm font-semibold text-[#6B7280]">
                        Created Date
                      </div>
                      <div className="text-sm text-[#111827]">
                        {isLoading ? (
                          <Skeleton className="w-24 h-5" />
                        ) : (
                          communityData.createdDate
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                <Button
                  variant="hollowGreen"
                  onClick={() => navigate(`/communities/view/${communityId}`)}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  {loading ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex">
            <button
              onClick={() => handleTabSwitch("general")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "general"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{ clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)" }}
            >
              General Info
            </button>
            <button
              onClick={() => handleTabSwitch("address")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "address"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{ clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)" }}
            >
              Address Info
            </button>
            <button
              onClick={() => handleTabSwitch("groups")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "groups"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              Groups
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 p-4 sm:p-6 mt-0 bg-white rounded-r-lg rounded-b-lg">
            {activeTab === "general" ? (
              <>
                {/* Status */}
                <div className="mb-4">
                  <Label>Status</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {["Active", "Dormant"].map((statusOption) => (
                      <div key={statusOption}>
                        {isLoading ? (
                          <Skeleton className="w-32 h-10" />
                        ) : (
                          <button
                            onClick={() => handleStatusChange(statusOption)}
                            className={`w-32 px-4 py-2 border font-semibold text-sm rounded ${
                              status === statusOption
                                ? statusOption === "Dormant"
                                  ? "bg-red-500 text-white border-gray-300"
                                  : "bg-[#14B8A6] text-white border-[#14B8A6]"
                                : "bg-white text-black border-gray-300 hover:bg-[#F0FDFA]"
                            }`}
                          >
                            {statusOption}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Community Admins */}
                <div>
                  <Label>Community Admins</Label>
                  {isLoading ? (
                    <Skeleton className="w-full h-10" />
                  ) : (
                    <MultiEmailSelect
                      selectedEmails={adminEmails}
                      onChange={(emails) => {
                        // Get current community data
                        const currentCommunityData =
                          getCommunityDetails.data?.[0] || {};
                        // Normalize emails before setting state
                        const normalizedEmails = emails.map((email) => {
                          const cleanEmail = email
                            .trim()
                            .replace(/\s*\([pP]ending\)\s*/g, "");
                          // Only add (Pending) if it's a new email not in the current admins
                          const isExisting = currentCommunityData.admins?.some(
                            (admin) =>
                              admin.rawEmail?.trim().toLowerCase() ===
                              cleanEmail.toLowerCase()
                          );
                          return isExisting
                            ? `${cleanEmail} (Pending)`
                            : cleanEmail;
                        });
                        setAdminEmails(emails);
                      }}
                      isMulti={true}
                    />
                  )}
                </div>

                {/* Community Name */}
                <div>
                  <Label>Community Name</Label>
                  {isLoading ? (
                    <Skeleton className="w-full h-10" />
                  ) : (
                    <Input
                      type="text"
                      name="communityName"
                      value={generalInfo.communityName}
                      onChange={handleGeneralInfoChange}
                      placeholder="Enter Community Name"
                    />
                  )}
                </div>

                {/* Description */}
                <div>
                  <Label>Description</Label>
                  {isLoading ? (
                    <Skeleton className="w-full h-10" />
                  ) : (
                    <Input
                      type="text"
                      name="description"
                      value={generalInfo.description}
                      onChange={handleGeneralInfoChange}
                      placeholder="Enter Description"
                    />
                  )}
                </div>
              </>
            ) : activeTab === "address" ? (
              <>
                {[
                  "addressLine1",
                  "addressLine2",
                  "postcode",
                  "city",
                  "state",
                  "country",
                ].map((field) => (
                  <div key={field}>
                    <Label>{field.replace(/([A-Z])/g, " $1").trim()}</Label>
                    {isLoading ? (
                      <Skeleton className="w-full h-10" />
                    ) : (
                      <Input
                        type="text"
                        name={field}
                        value={addressInfo[field]}
                        onChange={handleAddressInfoChange}
                        placeholder={`Enter ${field
                          .replace(/([A-Z])/g, " $1")
                          .trim()}`}
                      />
                    )}
                  </div>
                ))}
              </>
            ) : (
              // Groups Tab Content
              <div className="overflow-x-auto col-span-2 w-full md:max-w-[calc(100vw-375px)]">
                {getGroupDetails.loading ? (
                  <LoadingImage />
                ) : groupData.length > 0 ? (
                  <DataTable
                    columns={columns}
                    data={groupData}
                    actions={actions}
                  />
                ) : (
                  <div className="text-center text-gray-500 py-6">
                    No groups found.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Activate Modal */}
      <Modal
        isOpen={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        title="Activate Group"
        description="Are you sure you want to Activate this group?"
        icon={<FaExclamationTriangle className="text-green-500" />}
        iconColor="green"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowActivateModal(false)}
            >
              No, Cancel
            </Button>
            <Button onClick={handleConfirmActivate} disabled={loading}>
              {loading ? "Activating" : "Yes Activate this group"}
            </Button>
          </div>
        }
      ></Modal>

      {/* Deactivate Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Set Group Dormant"
        description="Are you sure you want to set this group as dormant?"
        icon={<FaExclamationTriangle className="text-red-500" />}
        iconColor="red"
        actions={
          <div className="flex justify-between w-full">
            <Button
              variant={"secondary"}
              onClick={() => setShowDeleteModal(false)}
            >
              No, Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={handleConfirmDeactivate}
              disabled={loading}
            >
              {loading ? "Saving..." : "Yes set this group dormant"}
            </Button>
          </div>
        }
      ></Modal>
    </div>
  );
};

export default EditCommunityPage;
